import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { UserDropdown } from '@src/manageUsers/components/UserDropdown';
import type { User } from '@src/manageUsers/domain/User';
import type { OrderListing } from '@src/orders/domain/OrderList';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import { OrderAlreadyClaimedException } from '@src/orders/exceptions/OrderAlreadyClaimedException';
import { useClaimPermit } from '@src/orders/hooks/useClaimPermit';
import { useUnclaimPermit } from '@src/orders/hooks/useUnclaimPermit';
import { useTranslation } from 'react-i18next';

export const AssignationPicker = ({
  order,
  summary,
  onAssign,
}: {
  order: OrderListing;
  summary: PermitSummary;
  onAssign: () => void;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { claimPermit } = useClaimPermit({
    orderId: order.id,
    permitId: summary.id,
    onSuccess: () => {
      onAssign();
      showToast({
        severity: ToastLevel.success,
        message: t('assignUser.success'),
      });
    },
    onError: (error: Error) => {
      if (error instanceof OrderAlreadyClaimedException) {
        showToast({
          severity: ToastLevel.error,
          message: t('assignUser.claimed'),
        });
      } else {
        showToast({
          severity: ToastLevel.error,
          message: t('assignUser.error'),
        });
      }
    },
  });

  const { unclaimPermit } = useUnclaimPermit({
    orderId: order.id,
    permitId: summary.id,
    onSuccess: () => {
      onAssign();
      showToast({
        severity: ToastLevel.success,
        message: t('unassignUser.success'),
      });
    },
    onError: () => {
      showToast({
        severity: ToastLevel.error,
        message: t('unassignUser.error'),
      });
    },
  });

  const assignUser = async (user: User | null) => {
    if (user) {
      claimPermit(user.id);
    } else if (summary.claimedBy) {
      unclaimPermit();
    }
  };

  const { user } = useAuthentication();
  const clientIsUser = isClientUser(user);
  const isDisabled = !summary.canModifyAssignee(clientIsUser);

  return (
    <UserDropdown
      className={dropdownStyle}
      variant="standard"
      selectedUser={summary.claimedBy}
      placeholder={t('unassigned')}
      disabled={isDisabled}
      disableUnderline
      onUserChange={assignUser}
    />
  );
};

const dropdownStyle = css({
  '& .MuiInputBase-root': {
    padding: `0 ${theme.spacing['3']}`,
    border: 0,
  },
  border: 0,
});
